import { z } from 'zod';

/**
 * Build the standard `page` + `pageSize` query parameters with endpoint-specific
 * bounds. Most lists want {@link PaginationParamsSchema}; reach for the factory
 * when a page is a different unit of work — a table row is not a session share.
 *
 * `maxPageSize` is a hard reject, not a clamp: a caller asking for more than an
 * endpoint will ever serve has a wrong mental model of the response, and a
 * silently trimmed page reads as "that was everything".
 *
 * Both fields are `coerce`d from strings so they parse cleanly out of
 * `request.query` without each route re-coercing by hand.
 */
export function paginationParamsSchema(bounds: { defaultPageSize: number; maxPageSize: number }) {
  return z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
      .number()
      .int()
      .min(1)
      .max(bounds.maxPageSize)
      .default(bounds.defaultPageSize),
  });
}

/**
 * Standard query-string parameters every paginated admin list endpoint
 * accepts. Offset-based (page + pageSize) — chosen over cursor pagination
 * for admin tables because page numbers, `n of total`, and direct jumps
 * are the natural affordance there. Cursor pagination is still preferred
 * for infinite-scroll feeds (see `ListAgentsQuerySchema`).
 */
export const PaginationParamsSchema = paginationParamsSchema({
  defaultPageSize: 20,
  maxPageSize: 100,
});
export type PaginationParams = z.infer<typeof PaginationParamsSchema>;

/**
 * Metadata describing the current page of a paginated response. Keep
 * `totalPages` server-derived so clients don't re-implement the
 * `Math.ceil(total / pageSize)` round-up edge cases.
 */
export const PaginationMetaSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
});
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

/**
 * Derive a response's page metadata from the window a route asked for and
 * the unsliced `total` its query counted. The one place the `totalPages`
 * round-up lives — including the empty case, where zero rows are zero
 * pages rather than one empty one.
 */
export function paginationMeta(counts: {
  page: number;
  pageSize: number;
  total: number;
}): PaginationMeta {
  const { page, pageSize, total } = counts;
  return { page, pageSize, total, totalPages: total === 0 ? 0 : Math.ceil(total / pageSize) };
}

/**
 * Helper that wraps an item schema in the standard `{ items, ...meta }`
 * envelope used by every paginated admin endpoint. Lets consumers write
 * `paginatedResponseSchema(OrganizationSchema)` instead of restating the
 * envelope shape per endpoint.
 */
export function paginatedResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.object({
    items: z.array(itemSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    totalPages: z.number().int().min(0),
  });
}

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}
